import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import {
  containerStyles,
  headingStyles,
  gridStyles,
  glassStyles,
  textFieldStyles,
  buttonStyles,
  contactInfoStyles,
  iconStyles,
  dividerStyles,
  infoTextStyles,
} from '../styles/Contact';

import {
  CONTACT_HEADING,
  PHONE,
  EMAIL,
  LOCATION,
  PHONE_LINK,
  EMAIL_LINK,
  MAP_LINK,
  CONTACT_NOTE,
} from '../data/Contact';

import { subscribeToPrefillMessage } from '../utils/contactHelper';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState(null); // null | 'sending'

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success', // 'success' or 'error'
    message: '',
  });

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Please fill out all required fields.',
      });
      return;
    }

    setStatus('sending');

    // Prepare form data as x-www-form-urlencoded
    const formDataEncoded = new URLSearchParams();
    formDataEncoded.append('name', formData.name);
    formDataEncoded.append('email', formData.email);
    formDataEncoded.append('message', formData.message);

    try {
      const response = await fetch('https://alsamashipping.com/send_mail.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataEncoded.toString(), // Use URLSearchParams encoded string
      });

      const data = await response.json(); // Parse JSON response

      if (data.success) {
        setSnackbar({
          open: true,
          severity: 'success',
          message: "Message sent successfully! We'll get back to you soon.",
        });
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSnackbar({
          open: true,
          severity: 'error',
          message: data.message || 'Failed to send message.',
        });
      }
    } catch (error) {
      console.error(error.message);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Unable to connect to the server. Please check your internet connection and try again.',
      });
    }
    setStatus(null);
  };

  const handleCloseSnackbar = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const unsubscribe = subscribeToPrefillMessage((newMessage) => {
      setFormData((prev) => ({
        ...prev,
        message: newMessage,
      }));

      // Optional: scroll to contact section
      const contactEl = document.getElementById('contact-container');
      if (contactEl) {
        contactEl.scrollIntoView({ behavior: 'smooth' });
      }
    });

    return unsubscribe;
  }, []);

  return (
    <Box id="contact-container" component="section" sx={containerStyles}>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Typography variant="h3" align="center" sx={headingStyles}>
          {CONTACT_HEADING}
        </Typography>

        <Box sx={gridStyles}>
          {/* Contact Form */}
          <Paper elevation={0} sx={glassStyles}>
            <form noValidate onSubmit={handleSubmit}>
              <Box sx={{ display: 'grid', gap: 4 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 3,
                  }}
                >
                  <TextField
                    fullWidth
                    label="Name"
                    variant="standard"
                    required
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    sx={textFieldStyles}
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    variant="standard"
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    sx={textFieldStyles}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Message"
                  variant="standard"
                  required
                  multiline
                  rows={5}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  sx={textFieldStyles}
                />

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={buttonStyles}
                  disabled={status === 'sending'}
                >
                  {status === 'sending' ? 'Sending...' : 'Send Message'}
                </Button>
              </Box>
            </form>
          </Paper>

          {/* Contact Info */}
          <Paper elevation={0} sx={glassStyles}>
            <Typography
              variant="h6"
              gutterBottom
              fontWeight={700}
              sx={{ mb: 3, color: 'common.white' }}
            >
              Get in touch
            </Typography>

            <Box component="a" href={PHONE_LINK} sx={contactInfoStyles}>
              <PhoneIcon sx={iconStyles} />
              <Typography variant="body1">{PHONE}</Typography>
            </Box>

            <Box component="a" href={EMAIL_LINK} sx={contactInfoStyles}>
              <EmailIcon sx={iconStyles} />
              <Typography variant="body1">{EMAIL}</Typography>
            </Box>

            <Box
              component="a"
              href={MAP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              sx={contactInfoStyles}
            >
              <LocationOnIcon sx={iconStyles} />
              <Typography variant="body1">{LOCATION}</Typography>
            </Box>

            <Divider sx={dividerStyles} />

            <Typography variant="body2" sx={infoTextStyles}>
              {CONTACT_NOTE}
            </Typography>
          </Paper>
        </Box>

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Contact;
